import { PublicFooter } from '../seo/PublicContent';
import { FC, ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  HomeIcon, 
  CameraIcon, 
  ClockIcon, 
  StarIcon, 
  UserIcon,
  PresentationChartLineIcon,
  Bars3Icon,
  XMarkIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline'
import { 
  HomeIcon as HomeIconSolid, 
  CameraIcon as CameraIconSolid, 
  ClockIcon as ClockIconSolid, 
  StarIcon as StarIconSolid, 
  UserIcon as UserIconSolid,
  PresentationChartLineIcon as PresentationChartLineIconSolid,
  ArchiveBoxIcon as ArchiveBoxIconSolid
} from '@heroicons/react/24/solid'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { useNewTour } from '../contexts/NewTourContext'
import Logo from './Logo'
import { UserRoleIndicator } from './UserRoleIndicator'

interface EnhancedLayoutProps {
  children: ReactNode
}

const EnhancedLayout: FC<EnhancedLayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { handleTargetClick, advanceTourAfterNavigation, isActive: tourIsActive } = useNewTour()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Navigation items
  const navigation = [
    { 
      name: 'Home', 
      href: '/', 
      icon: HomeIcon, 
      iconSolid: HomeIconSolid 
    },
    { 
      name: 'Analyze', 
      href: '/analyze', 
      icon: CameraIcon,
      iconSolid: CameraIconSolid
    },
    { 
      name: 'Analytics', 
      href: '/analytics', 
      icon: PresentationChartLineIcon, 
      iconSolid: PresentationChartLineIconSolid 
    },
    { 
      name: 'History', 
      href: '/history', 
      icon: ClockIcon, 
      iconSolid: ClockIconSolid 
    },
    { 
      name: 'Favorites', 
      href: '/favorites', 
      icon: StarIcon, 
      iconSolid: StarIconSolid 
    },
    { 
      name: 'Inventory', 
      href: '/inventory', 
      icon: ArchiveBoxIcon, 
      iconSolid: ArchiveBoxIconSolid 
    },
    { 
      name: 'Profile', 
      href: '/profile', 
      icon: UserIcon, 
      iconSolid: UserIconSolid 
    },
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex md:w-64 md:flex-col">
          <div className="flex flex-col flex-grow pt-5 overflow-y-auto bg-white/95 dark:bg-gray-800/95 border-r border-gray-200/50 dark:border-gray-700/50 shadow-lg">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 px-6 mb-8">
              <Logo className="h-8 w-auto" showText={true} />
            </div>

            {/* Navigation */}
            <div className="flex-grow flex flex-col px-4">
              <nav className="flex-1 space-y-2">
                {navigation.map((item) => {
                  const Icon = isActive(item.href) ? item.iconSolid : item.icon
                  const tourId = `tour-${item.name.toLowerCase().replace(' ', '-')}-nav`
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-tour={tourId}
                      onClick={(e) => {
                        // If tour is active, handle tour click and prevent navigation
                        if (tourIsActive) {
                          e.preventDefault()
                          console.log(`🎯 Tour: Handling click on ${tourId}`)
                          
                          // Special handling for demo workflow navigation items
                          if (tourId === 'tour-analyze-nav' || tourId === 'tour-history-nav' || tourId === 'tour-saved-nav') {
                            console.log(`🎯 Tour: Demo workflow nav ${tourId}, navigating then advancing`)
                            navigate(item.href)
                            
                            // Advance tour after navigation with delay for page loading
                            setTimeout(() => {
                              console.log('🎯 Tour: Advancing to next step after navigation')
                              advanceTourAfterNavigation()
                            }, 800)
                          } else if (tourId.includes('-nav')) {
                            // For other navigation items during tour, just advance without navigating
                            console.log(`🎯 Tour: Non-demo nav ${tourId} clicked, advancing tour only`)
                            handleTargetClick(tourId)
                          } else {
                            // For non-navigation items, handle normally
                            handleTargetClick(tourId)
                          }
                          return
                        }
                        // Otherwise, allow normal navigation
                      }}
                      className={`group flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                        isActive(item.href)
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/50 dark:hover:text-white hover:scale-105'
                      }`}
                    >
                      <Icon className="mr-3 flex-shrink-0 h-5 w-5" />
                      {item.name}
                    </Link>
                  )
                })}
              </nav>

              {/* User section */}
              <div className="flex-shrink-0 border-t border-gray-200/50 dark:border-gray-700/50 p-4 mt-4">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    {user && <UserRoleIndicator />}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={toggleTheme}
                      className="p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-200"
                    >
                      {theme === 'dark' ? '🌞' : '🌙'}
                    </button>
                    {user && (
                      <button
                        onClick={signOut}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-200"
                      >
                        Sign Out
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          {isMobileMenuOpen && (
            <div className="fixed inset-0 flex z-40">
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
              <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  >
                    <XMarkIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                  <div className="flex-shrink-0 flex items-center px-4">
                    <Logo className="h-8 w-auto" />
                  </div>
                  <nav className="mt-5 px-2 space-y-1">
                    {navigation.map((item) => {
                      const Icon = isActive(item.href) ? item.iconSolid : item.icon
                      const tourId = `tour-${item.name.toLowerCase().replace(' ', '-')}-nav`
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={(e) => {
                            // If tour is active, handle tour click and prevent navigation
                            if (tourIsActive) {
                              e.preventDefault()
                              
                              // Special handling for demo workflow navigation items
                              if (tourId === 'tour-analyze-nav' || tourId === 'tour-history-nav' || tourId === 'tour-saved-nav') {
                                navigate(item.href)
                                setTimeout(() => {
                                  advanceTourAfterNavigation()
                                }, 800)
                              } else if (tourId.includes('-nav')) {
                                // For other navigation items during tour, just advance without navigating
                                handleTargetClick(tourId)
                              } else {
                                // For non-navigation items, handle normally
                                handleTargetClick(tourId)
                              }
                              return
                            }
                            // Otherwise, close mobile menu and allow normal navigation
                            setIsMobileMenuOpen(false)
                          }}
                          data-tour={tourId}
                          className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                            isActive(item.href)
                              ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                          }`}
                        >
                          <Icon className="mr-4 flex-shrink-0 h-6 w-6" />
                          {item.name}
                        </Link>
                      )
                    })}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Mobile header */}
          <div className="md:hidden">
            <div className="relative z-10 flex-shrink-0 flex h-16 bg-white/95 dark:bg-gray-800/95 shadow-lg border-b border-gray-200/50 dark:border-gray-700/50">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="px-4 border-r border-gray-200/50 dark:border-gray-700/50 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors touch-target"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="flex-1 px-4 flex justify-between">
                <div className="flex-1 flex">
                  <div className="flex items-center">
                    <Logo className="h-8 w-auto" />
                  </div>
                </div>
                <div className="ml-4 flex items-center md:ml-6 space-x-2">
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-200 touch-target"
                  >
                    {theme === 'dark' ? '🌞' : '🌙'}
                  </button>
                  {user && (
                    <button
                      onClick={signOut}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-3 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all duration-200 touch-target"
                    >
                      Sign Out
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            {children}
          </main>
          <PublicFooter />
        </div>
      </div>
    </div>
  )
}

export default EnhancedLayout
